import { useEffect, useRef, useState } from 'react'
import { FileUp } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { Chip } from '../interviews/Chip'
import { useGetContentPlanImports, useUploadContentPlan } from '../../hooks/backend/marketing'
import { writeErrorMessage } from '../../utils/writeError'
import { fileToBase64, monthLabel, shortDate } from '../../utils/marketing'

type ImportListRow = {
  id: string
  status: string
  period_month: string | null
  files: { filename: string }[]
  created_by: string
  created_at: string
  error: string | null
  posts: number
}

const STATUS_TONE: Record<string, 'green' | 'amber' | 'neutral' | 'red' | 'brand'> = {
  Reading: 'brand',
  'Pending Review': 'amber',
  Committed: 'green',
  Discarded: 'neutral',
  Failed: 'red',
}

/**
 * Upload a month's plan, and the recent uploads.
 *
 * Several files at once, because a plan is usually a calendar and one or two
 * caption documents, and the reader matches captions to posts across them. The
 * month is optional: the reader takes it from the document, and the review lets
 * a person set it if the document does not say.
 */
export function PlanUpload({ onOpen }: { onOpen: (id: string) => void }) {
  const upload = useUploadContentPlan()
  const list = useGetContentPlanImports()
  const [files, setFiles] = useState<File[]>([])
  const [month, setMonth] = useState('')
  const [busy, setBusy] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  const trigger = list.trigger
  useEffect(() => {
    void trigger({}, { skipCache: true })
  }, [trigger])

  async function send() {
    setBusy(true)
    try {
      const payload = await Promise.all(
        files.map(async (f) => ({ filename: f.name, contentType: f.type || undefined, contentBase64: await fileToBase64(f) })),
      )
      const res = (await upload.trigger({ files: payload, period_month: month ? `${month}-01` : null }).result) as {
        id: string
      }
      toast.success('Uploaded. Reading it now; this takes a minute or two.')
      setFiles([])
      if (input.current) input.current.value = ''
      onOpen(res.id)
    } catch (err) {
      toast.error('The plan was not uploaded', { description: writeErrorMessage(err) })
    } finally {
      setBusy(false)
    }
  }

  const rows = (list.data as ImportListRow[] | null) ?? []

  return (
    <section className="space-y-3 border-b border-border px-4 py-4">
      <div>
        <p className="text-sm font-semibold text-foreground">Upload a month's plan</p>
        <p className="text-xs text-muted-foreground">
          The calendar and its caption documents, as Word, PDF or Excel. Nothing goes onto the board until you have
          checked what was read and pressed Commit.
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Files</Label>
          <Input
            ref={input}
            type="file"
            multiple
            accept=".docx,.pdf,.xlsx"
            className="h-9 max-w-xs text-xs"
            onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Month, if the document does not say</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40" />
        </div>
        <Button type="button" size="sm" variant="brand" className="gap-1.5" disabled={busy || files.length === 0} onClick={() => void send()}>
          {busy ? <Spinner className="h-3.5 w-3.5" /> : <FileUp className="h-3.5 w-3.5" />}
          Upload and read
        </Button>
      </div>
      {files.length > 0 && (
        <p className="text-[11px] text-muted-foreground">{files.map((f) => f.name).join(', ')}</p>
      )}

      {rows.length > 0 && (
        <ul className="divide-y divide-border rounded-md border border-border">
          {rows.slice(0, 6).map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => onOpen(r.id)}
                className="flex w-full flex-wrap items-center gap-2 px-3 py-2 text-left hover:bg-muted/50"
              >
                <Chip tone={STATUS_TONE[r.status] ?? 'neutral'}>{r.status}</Chip>
                <span className="text-xs font-medium text-foreground">
                  {r.period_month ? monthLabel(r.period_month) : 'Month not set'}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {r.posts} post{r.posts === 1 ? '' : 's'}, {r.files.map((f) => f.filename).join(', ')}
                </span>
                <span className="ml-auto text-[11px] text-muted-foreground">{shortDate(r.created_at)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
